import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getValidationItems,
  updateValidationItem,
  bulkUpdateValidationItems,
  ValidationItem,
} from '@wove/api'
import { ValidationItemStatus, ValidationItemAction } from '@wove/common'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Spinner,
  Alert,
  cn,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
} from '@wove/web-ui'
import {
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  CheckCheck,
  Filter,
} from 'lucide-react'
import { format } from 'date-fns'

interface ValidationItemsPanelProps {
  shipmentId: string
}

export const ValidationItemsPanel: React.FC<ValidationItemsPanelProps> = ({ shipmentId }) => {
  const queryClient = useQueryClient()
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterActionStatus, setFilterActionStatus] = useState<string>('all')
  const [actionModalOpen, setActionModalOpen] = useState(false)
  const [actionType, setActionType] = useState<ValidationItemAction | null>(null)
  const [actionReason, setActionReason] = useState('')
  const [validatedFieldsExpanded, setValidatedFieldsExpanded] = useState(false)
  const [dismissModalOpen, setDismissModalOpen] = useState(false)
  const [dismissingItem, setDismissingItem] = useState<ValidationItem | null>(null)
  const [dismissReason, setDismissReason] = useState('')

  // Fetch validation items
  const { data, isLoading, error } = useQuery({
    queryKey: ['shipment', shipmentId, 'validation-items', filterStatus, filterActionStatus],
    queryFn: () =>
      getValidationItems(shipmentId, {
        ...(filterStatus !== 'all' && { status: filterStatus as ValidationItemStatus }),
        ...(filterActionStatus !== 'all' && {
          actionStatus: filterActionStatus as ValidationItemAction,
        }),
      }),
  })

  // Update single item mutation
  const updateItemMutation = useMutation({
    mutationFn: (params: { itemId: string; actionStatus: ValidationItemAction; reason?: string }) =>
      updateValidationItem(shipmentId, params.itemId, {
        actionStatus: params.actionStatus,
        actionReason: params.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId, 'validation-items'] })
    },
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (params: {
      itemIds: string[]
      actionStatus: ValidationItemAction
      reason?: string
    }) =>
      bulkUpdateValidationItems(shipmentId, {
        itemIds: params.itemIds,
        actionStatus: params.actionStatus,
        actionReason: params.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId, 'validation-items'] })
      setSelectedItems(new Set())
    },
  })

  const handleSelectAll = () => {
    if (!data?.items) return

    // Only select items that are pending action AND not validated
    const selectableItems = data.items.filter(
      (item) =>
        item.actionStatus === ValidationItemAction.PENDING &&
        item.status !== ValidationItemStatus.VALIDATED,
    )

    if (selectedItems.size === selectableItems.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(selectableItems.map((item) => item.id)))
    }
  }

  const handleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId)
    } else {
      newSelected.add(itemId)
    }
    setSelectedItems(newSelected)
  }

  const handleToggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId)
    } else {
      newExpanded.add(itemId)
    }
    setExpandedItems(newExpanded)
  }

  const handleQuickAction = (item: ValidationItem, action: ValidationItemAction) => {
    if (action === ValidationItemAction.DISMISSED) {
      // Show dismiss modal for reason
      setDismissingItem(item)
      setDismissModalOpen(true)
    } else {
      // Direct action without reason
      updateItemMutation.mutate({
        itemId: item.id,
        actionStatus: action,
      })
    }
  }

  const handleDismissWithReason = () => {
    if (!dismissingItem) return

    updateItemMutation.mutate({
      itemId: dismissingItem.id,
      actionStatus: ValidationItemAction.DISMISSED,
      reason: dismissReason,
    })

    setDismissModalOpen(false)
    setDismissingItem(null)
    setDismissReason('')
  }

  const handleBulkAction = () => {
    if (!actionType || selectedItems.size === 0) return

    bulkUpdateMutation.mutate({
      itemIds: Array.from(selectedItems),
      actionStatus: actionType,
      reason: actionReason,
    })

    setActionModalOpen(false)
    setActionReason('')
  }

  const getStatusConfig = (status: ValidationItemStatus) => {
    switch (status) {
      case ValidationItemStatus.CRITICAL:
        return { icon: AlertCircle, color: 'text-red-500', badgeVariant: 'error' as const }
      case ValidationItemStatus.WARNING:
        return { icon: AlertCircle, color: 'text-amber-500', badgeVariant: 'warning' as const }
      case ValidationItemStatus.VALIDATED:
        return { icon: CheckCircle, color: 'text-green-500', badgeVariant: 'success' as const }
      default:
        return { icon: AlertCircle, color: 'text-gray-500', badgeVariant: 'secondary' as const }
    }
  }

  const getActionConfig = (action: ValidationItemAction) => {
    switch (action) {
      case ValidationItemAction.DISMISSED:
        return { icon: X, label: 'Dismissed', color: 'text-gray-500' }
      case ValidationItemAction.APPLIED:
        return { icon: CheckCheck, label: 'Applied', color: 'text-green-600' }
      case ValidationItemAction.IGNORED:
        return { icon: EyeOff, label: 'Ignored', color: 'text-gray-400' }
      default:
        return { icon: Eye, label: 'Pending', color: 'text-blue-600' }
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="error">
        <AlertCircle className="h-4 w-4" />
        <div>Failed to load validation items</div>
      </Alert>
    )
  }

  const items = data?.items || []
  const pendingItems = items.filter((item) => item.actionStatus === ValidationItemAction.PENDING)
  const itemsNeedingAttention = pendingItems.filter(
    (item) =>
      item.status === ValidationItemStatus.CRITICAL || item.status === ValidationItemStatus.WARNING,
  )
  // Only non-validated pending items can be selected
  const selectableItems = pendingItems.filter(
    (item) => item.status !== ValidationItemStatus.VALIDATED,
  )

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Validation Items</h3>
            <p className="text-sm text-gray-600 mt-1">
              {itemsNeedingAttention.length > 0
                ? `${itemsNeedingAttention.length} items need attention`
                : 'All items reviewed'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value={ValidationItemStatus.CRITICAL}>Critical</SelectItem>
                <SelectItem value={ValidationItemStatus.WARNING}>Warning</SelectItem>
                <SelectItem value={ValidationItemStatus.VALIDATED}>Validated</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterActionStatus} onValueChange={setFilterActionStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value={ValidationItemAction.PENDING}>Pending</SelectItem>
                <SelectItem value={ValidationItemAction.DISMISSED}>Dismissed</SelectItem>
                <SelectItem value={ValidationItemAction.APPLIED}>Applied</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bulk actions */}
        {selectedItems.size > 0 && (
          <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setActionType(ValidationItemAction.DISMISSED)
                  setActionModalOpen(true)
                }}
              >
                Dismiss
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setActionType(ValidationItemAction.APPLIED)
                  setActionModalOpen(true)
                }}
              >
                Mark Applied
              </Button>
            </div>
          </div>
        )}

        {/* Select all */}
        {selectableItems.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox
              checked={selectableItems.length > 0 && selectedItems.size === selectableItems.length}
              onChange={handleSelectAll}
            />
            <span>Select all {selectableItems.length} items</span>
          </div>
        )}

        {/* Validated fields section */}
        {items.filter((item) => item.status === ValidationItemStatus.VALIDATED).length > 0 && (
          <div className="space-y-2">
            <button
              onClick={() => setValidatedFieldsExpanded(!validatedFieldsExpanded)}
              className="flex items-center gap-2 hover:bg-gray-50 p-1 -m-1 rounded transition-colors w-full text-left"
            >
              {validatedFieldsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <CheckCircle className="h-4 w-4 text-green-500" />
              <h4 className="text-sm font-medium text-gray-700">Validated Fields</h4>
              <span className="text-xs text-gray-500">
                ({items.filter((item) => item.status === ValidationItemStatus.VALIDATED).length})
              </span>
            </button>
            {validatedFieldsExpanded && (
              <Card className="bg-green-50/50 border-green-200">
                <CardContent className="p-3">
                  <div className="space-y-2">
                    {items
                      .filter((item) => item.status === ValidationItemStatus.VALIDATED)
                      .map((item) => (
                        <div key={item.id} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">{item.fieldName}</p>
                            {item.reason && (
                              <p className="text-xs text-gray-600 mt-0.5">{item.reason}</p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Issues list */}
        <div className="space-y-2">
          {items.filter((item) => item.status !== ValidationItemStatus.VALIDATED).length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <h4 className="text-sm font-medium text-gray-700">Issues to Review</h4>
              <span className="text-xs text-gray-500">
                ({items.filter((item) => item.status !== ValidationItemStatus.VALIDATED).length})
              </span>
            </div>
          )}

          {items
            .filter((item) => item.status !== ValidationItemStatus.VALIDATED)
            .map((item) => {
              const statusConfig = getStatusConfig(item.status)
              const actionConfig = getActionConfig(item.actionStatus)
              const StatusIcon = statusConfig.icon
              const ActionIcon = actionConfig.icon
              const isExpanded = expandedItems.has(item.id)

              return (
                <Card
                  key={item.id}
                  className={cn(
                    'transition-all',
                    item.actionStatus !== ValidationItemAction.PENDING && 'opacity-60',
                  )}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {item.actionStatus === ValidationItemAction.PENDING ? (
                        <Checkbox
                          checked={selectedItems.has(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      ) : (
                        <div className="w-5" />
                      )}

                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between">
                          <div
                            className="flex items-start gap-2 cursor-pointer flex-1"
                            onClick={() => handleToggleExpanded(item.id)}
                          >
                            <button className="mt-0.5">
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                            <StatusIcon className={cn('h-5 w-5 mt-0.5', statusConfig.color)} />
                            <div className="flex-1">
                              <h4 className="font-medium">{item.fieldName}</h4>
                              {item.reason && (
                                <p className="text-sm text-gray-600 mt-1">{item.reason}</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Badge variant={statusConfig.badgeVariant}>{item.status}</Badge>
                            {item.actionStatus !== ValidationItemAction.PENDING && (
                              <div className="flex items-center gap-1">
                                <ActionIcon className={cn('h-4 w-4', actionConfig.color)} />
                                <span className={cn('text-xs', actionConfig.color)}>
                                  {actionConfig.label}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="ml-7 space-y-3 pt-2 border-t">
                            {item.suggestion && (
                              <div>
                                <p className="text-xs font-medium text-gray-700">Suggestion</p>
                                <p className="text-sm text-gray-600 mt-1">{item.suggestion}</p>
                              </div>
                            )}

                            {item.files && item.files.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-gray-700">Affected Files</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.files.map((file, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {file}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {item.actionStatus !== ValidationItemAction.PENDING &&
                              item.actionTakenAt && (
                                <div className="text-xs text-gray-500">
                                  <p>
                                    {actionConfig.label} on{' '}
                                    {format(new Date(item.actionTakenAt), 'MMM d, yyyy h:mm a')}
                                  </p>
                                  {item.actionReason && (
                                    <p className="mt-1">Reason: {item.actionReason}</p>
                                  )}
                                </div>
                              )}

                            {item.actionStatus === ValidationItemAction.PENDING && (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleQuickAction(item, ValidationItemAction.DISMISSED)
                                  }
                                >
                                  Dismiss
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleQuickAction(item, ValidationItemAction.APPLIED)
                                  }
                                >
                                  Mark Applied
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
        </div>

        {items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>No validation items found</p>
          </div>
        )}
      </div>

      {/* Bulk action modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Validation Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              You are about to update {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''}.
            </p>
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                className="mt-1"
                placeholder="Add a reason for this action..."
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActionModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkAction} loading={bulkUpdateMutation.isPending}>
              Update Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss modal */}
      <Dialog open={dismissModalOpen} onOpenChange={setDismissModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss Validation Issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {dismissingItem && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">You are dismissing the following issue:</p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-sm">{dismissingItem.fieldName}</p>
                  {dismissingItem.reason && (
                    <p className="text-sm text-gray-600 mt-1">{dismissingItem.reason}</p>
                  )}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Reason for dismissal (optional)</label>
              <Textarea
                className="mt-1"
                placeholder="Add a reason for dismissing this issue..."
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setDismissModalOpen(false)
                setDismissingItem(null)
                setDismissReason('')
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleDismissWithReason} loading={updateItemMutation.isPending}>
              Dismiss Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
